import { useEffect, useRef } from "react";
import { planStrokes } from "../../lib/strokePlan";
import { detectCrop } from "../../lib/borderCrop";

const ease = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : 0.5 - 0.5 * Math.cos(Math.PI * x));
// Where the painting sits once it has emerged: about 70–75% of the viewport height, with only the nav above and her line and buttons below.
export function revealBox() {
  const w = window.innerWidth, h = window.innerHeight;
  const short = h < 500;
  const top = short ? 56 : Math.max(64, Math.round(h * 0.09));
  const bottom = short ? 104 : Math.max(128, Math.round(h * 0.18));
  return { top, bottom: h - bottom, left: 12, right: w - 12, short };
}

// Catmull-Rom densified path, so a stroke sweeps along a smooth curve rather than a few straight jumps.
function densify(pts, per = 3) {
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    for (let k = 0; k < per; k++) {
      const t = k / per, t2 = t * t, t3 = t2 * t;
      out.push([0, 1].map((d) => 0.5 * (2 * p1[d] + (-p0[d] + p2[d]) * t + (2 * p0[d] - 5 * p1[d] + 4 * p2[d] - p3[d]) * t2 + (-p0[d] + 3 * p1[d] - 3 * p2[d] + p3[d]) * t3)));
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}
function pointAt(br, s) {
  const L = br.cum, n = L.length - 1, target = s * L[n];
  let j = br.j || 0;
  while (j < n - 1 && L[j + 1] < target) j++;
  while (j > 0 && L[j] > target) j--;
  br.j = j;
  const seg = L[j + 1] - L[j] || 1, f = Math.min(1, Math.max(0, (target - L[j]) / seg));
  return [j, br.line[j][0] + (br.line[j + 1][0] - br.line[j][0]) * f, br.line[j][1] + (br.line[j + 1][1] - br.line[j][1]) * f];
}

// Her real painting, painted in before your eyes: bristly strokes sweeping along their paths to reveal the gallery image's own pixels. A tap finishes it.
export function StrokeReveal({ src, cfg, clock, at, onDone }) {
  const cv = useRef();
  const dim = useRef();
  useEffect(() => {
    const canvas = cv.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    let raf, plan, paths, fit, pattern, done = false, wash = 0, last = 0;
    const state = (window.__afaeReveal = { started: false, progress: 0, done: false, skipped: false, strokes: 0 });
    const full = () => fit && ctx.drawImage(img, fit.sx, fit.sy, fit.sw, fit.sh, fit.x, fit.y, fit.w, fit.h);
    const finish = (skipped) => {
      if (done) return;
      done = true;
      ctx.globalAlpha = 1;
      full();
      dim.current.style.opacity = 0.94;
      canvas.style.pointerEvents = "none";
      // the last stroke lands: a soft bloom of light that settles
      canvas.animate?.([{ filter: "brightness(1.16) saturate(1.08)" }, { filter: "brightness(1) saturate(1)" }], { duration: 1600, easing: "ease-out" });
      Object.assign(state, { done: true, progress: 1, skipped });
      onDone?.();
    };
    const layout = () => {
      const dpr = Math.min(1.5, window.devicePixelRatio || 1);
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      const c = detectCrop(img);
      const iw = img.naturalWidth, ih = img.naturalHeight;
      const sx = c[0] * iw, sy = (1 - c[3]) * ih, sw = (c[2] - c[0]) * iw, sh = (c[3] - c[1]) * ih;
      const box = revealBox();
      const bw = (box.right - box.left) * dpr, bh = (box.bottom - box.top) * dpr;
      const s = Math.min(bw / sw, bh / sh);
      fit = { sx, sy, sw, sh, w: sw * s, h: sh * s };
      fit.x = box.left * dpr + (bw - fit.w) / 2;
      fit.y = box.top * dpr + (bh - fit.h) / 2;
      window.__afaeRevealBox = { top: fit.y / dpr, bottom: (fit.y + fit.h) / dpr, left: fit.x / dpr, right: (fit.x + fit.w) / dpr };
      window.dispatchEvent(new Event("afae-reveal-box"));
      const ox = fit.x - sx * s, oy = fit.y - sy * s;
      ctx.save();
      ctx.beginPath();
      ctx.rect(fit.x, fit.y, fit.w, fit.h);
      ctx.clip();
      const pat = ctx.createPattern(img, "no-repeat");
      pat.setTransform(new DOMMatrix([s, 0, 0, s, ox, oy]));
      ctx.strokeStyle = pattern = pat;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const k = (iw * s) / plan.W;
      // pre-computed: each stroke is a few hairs with ragged ends along a smoothed path, in canvas pixels
      paths = plan.strokes.map((st) => {
        const pts = densify(st.pts.map(([x, y]) => [ox + x * k, oy + y * k]));
        const nb = 5;
        return Array.from({ length: nb }, (_, b) => {
          const r = Math.sin(st.seed * 31.7 + b * 12.9) * 0.5 + 0.5;
          const off = ((b + 0.5) / nb - 0.5) * st.w * k * 0.9;
          const line = pts.map((p, i) => {
            const q = pts[Math.min(pts.length - 1, i + 1)], o = pts[Math.max(0, i - 1)];
            const dx = q[0] - o[0], dy = q[1] - o[1], l = Math.hypot(dx, dy) || 1;
            return [p[0] - (dy / l) * off, p[1] + (dx / l) * off];
          });
          const cum = [0];
          for (let i = 1; i < line.length; i++) cum.push(cum[i - 1] + Math.hypot(line[i][0] - line[i - 1][0], line[i][1] - line[i - 1][1]));
          return { line, cum, w: st.w * k * (0.34 + 0.14 * r), a: 0.06 * r, b: 1 - 0.08 * (1 - r), s: 0 };
        });
      });
    };
    // each hair grows from where it was to where it is now, along its own path: one smooth sweep, never a pop
    const sweep = (br, s1) => {
      const s0 = br.s;
      if (s1 <= s0) return;
      br.s = s1;
      const [j0, x0, y0] = pointAt(br, s0);
      const [j1, x1, y1] = pointAt(br, s1);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      for (let j = j0 + 1; j <= j1; j++) ctx.lineTo(br.line[j][0], br.line[j][1]);
      ctx.lineTo(x1, y1);
      const taper = 0.6 + 0.4 * Math.sin(Math.PI * Math.min(1, (s0 + s1) / 2));
      if (cfg.ember) {
        ctx.strokeStyle = "rgba(255,196,80,0.45)";
        ctx.lineWidth = br.w * taper * 1.5;
        ctx.stroke();
        ctx.strokeStyle = pattern;
      }
      // a feathered bristle edge, then its body
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = br.w * taper * 1.7;
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.lineWidth = br.w * taper;
      ctx.stroke();
    };
    const draw = (e) => {
      for (let i = 0; i < plan.strokes.length; i++) {
        const st = plan.strokes[i];
        if (e < st.t0) continue;
        const p = ease((e - st.t0) / st.dur);
        const hairs = paths[i];
        if (hairs[0].done) continue;
        for (const br of hairs) sweep(br, Math.min(br.b, Math.max(br.a, br.a + (br.b - br.a) * p)));
        if (p >= 1) hairs[0].done = true;
      }
    };
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!plan || !paths || done) return;
      const raw = (clock.current?.t ?? 0) - at;
      if (raw <= 0) return;
      if (!state.started) {
        state.started = true;
        canvas.style.pointerEvents = "auto";
      }
      // eased overall cadence: a gentle start, a steady middle, a soft landing
      const x = Math.min(1, raw / plan.D);
      const e = plan.D * (0.45 * x + 0.55 * ease(x));
      if (e > last) draw(e);
      last = e;
      state.progress = x;
      dim.current.style.opacity = Math.min(0.94, x * 1.4);
      // a last soft wash fills any gaps the brush left, as a smooth cross-fade
      if (x > 0.86) {
        const a = ease((x - 0.86) / 0.14);
        if (a > wash) {
          ctx.globalAlpha = Math.min(1, (a - wash) / (1 - wash + 1e-3));
          full();
          ctx.globalAlpha = 1;
          wash = a;
        }
      }
      if (x >= 1) finish(false);
    };
    img.onload = () => {
      plan = planStrokes(img, cfg);
      state.strokes = plan.strokes.length;
      layout();
    };
    img.src = src;
    raf = requestAnimationFrame(tick);
    const onTap = (ev) => {
      ev.stopPropagation();
      finish(true);
    };
    canvas.addEventListener("pointerdown", onTap);
    const onResize = () => {
      if (!plan) return;
      if (state.started) finish(true);
      ctx.restore();
      layout();
      if (done) full();
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointerdown", onTap);
      window.removeEventListener("resize", onResize);
      delete window.__afaeReveal;
      delete window.__afaeRevealBox;
    };
  }, [src, cfg, clock, at]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="stroke-reveal" className="pointer-events-none absolute inset-0">
      <div ref={dim} className="absolute inset-0 bg-[#0B0A12]" style={{ opacity: 0, transition: "opacity 0.3s linear" }} />
      <canvas ref={cv} data-testid="stroke-reveal-canvas" className="absolute inset-0 h-full w-full" style={{ pointerEvents: "none", touchAction: "none" }} />
    </div>
  );
}
